// @flow strict

import React from "react";
import type {
  LocalDigitos,
  RegisteredStore,
  Equipo,
} from "~robin-hood/redux/store";
import {
  equipoWithPrueba,
  localStoreWithPrueba,
} from "~robin-hood/redux/store";
import styles from "./PruebaDigitos.module.css";
import window from "~robin-hood/animations/window.module.css";
import { imgSrc } from "~common/utils";
import { playersMap } from "~robin-hood/utils";
import type { Player } from "~robin-hood/utils";
import classnames from "~common/classnames";

type Props = {
  player: Player,
  equipo: Equipo,
  updateEquipo: (Equipo) => void,
  store: RegisteredStore,
  updateLocalStore: (store: RegisteredStore) => void,
};

const DIGITOS_IMG_PLAYER = playersMap([
  imgSrc("/images/robin-hood/prueba-digitos/digitos1.png"),
  imgSrc("/images/robin-hood/prueba-digitos/digitos2.png"),
  imgSrc("/images/robin-hood/prueba-digitos/digitos3.png"),
  imgSrc("/images/robin-hood/prueba-digitos/digitos4.png"),
  imgSrc("/images/robin-hood/prueba-digitos/digitos5.png"),
  imgSrc("/images/robin-hood/prueba-digitos/digitos6.png"),
]);

const DIGITOS_SECRET = [12, 88, 95];

export function PruebaDigitos(props: Props): React$Node {
  const { player, equipo, updateEquipo, store, updateLocalStore } = props;
  return (
    <div className={classnames(styles.pruebaDigitosWrapper, window.scaleInBl)}>
      <div className={classnames(styles.pruebaDigitos)}>
        <img
          alt="Digitos"
          className={styles.image}
          src={DIGITOS_IMG_PLAYER.get(player)}
        />
      </div>
      <Solucion
        player={player}
        equipo={equipo}
        updateEquipo={updateEquipo}
        store={store}
        updateLocalStore={updateLocalStore}
      />
    </div>
  );
}

type SolucionProps = {
  player: Player,
  store: RegisteredStore,
  updateLocalStore: (RegisteredStore) => void,
  equipo: Equipo,
  updateEquipo: (Equipo) => void,
};

class Solucion extends React.PureComponent<SolucionProps> {
  _doCheck = () => {
    const { store, equipo, updateEquipo, player } = this.props;
    const values = valueFromLocal(store, player);
    const isCorrect = correctFromEquipo(equipo);
    const correct = values.every((v, idx) => {
      return DIGITOS_SECRET[idx] === v;
    });
    if (correct && correct !== isCorrect) {
      const updated = equipoWithPrueba(equipo, {
        digitos: { value: values, correct },
      });
      updateEquipo(updated);
    }
  };

  // On change we won't connect with server
  _onDigitoChange = (digito: 0 | 1 | 2): ((number, number) => void) => {
    return (first: number, second: number) => {
      const { store, updateLocalStore, player } = this.props;
      const values = valueFromLocal(store, player);
      const value = first * 10 + second;
      if (values[digito] === value) {
        return;
      }
      const newValues = [...values];
      newValues[digito] = parseInt(value, 10);
      updateLocalStore(
        updateLocalWithDigitos(store, player, { value: newValues })
      );
    };
  };

  render(): React$Node {
    const { equipo, store, player } = this.props;
    const correct = correctFromEquipo(equipo);
    const values = correct
      ? valueFromEquipo(equipo)
      : valueFromLocal(store, player);
    return (
      <div className={styles.solucion}>
        <SolucionDigito
          className={styles.red}
          correct={correct}
          defaultValue={values[0]}
          onChange={this._onDigitoChange(0)}
        />
        <SolucionDigito
          className={styles.green}
          correct={correct}
          defaultValue={values[1]}
          onChange={this._onDigitoChange(1)}
        />
        <SolucionDigito
          className={styles.blue}
          correct={correct}
          defaultValue={values[2]}
          onChange={this._onDigitoChange(2)}
        />
        <DigitosCheck correct={correct} onCheck={this._doCheck} />
      </div>
    );
  }
}

type DigitoProps = {
  correct: boolean,
  className?: string,
  defaultValue: number,
  onChange: (first: number, second: number) => void,
};

function SolucionDigito(props: DigitoProps) {
  const first = firstDigit(props.defaultValue);
  const second = secondDigit(props.defaultValue);
  return (
    <div
      className={classnames(
        styles.solucionDigito,
        props.className,
        props.correct && styles.correct
      )}
    >
      <span
        className={styles.firstDigit}
        onClick={() => {
          !props.correct &&
            props.onChange &&
            props.onChange((first + 1) % 10, second);
        }}
      >
        {first}
        <span className={styles.behindDigito}>0</span>
      </span>
      <span
        className={styles.secondDigit}
        onClick={() => {
          !props.correct &&
            props.onChange &&
            props.onChange(first, (second + 1) % 10);
        }}
      >
        {second}
        <span className={styles.behindDigito}>0</span>
      </span>
    </div>
  );
}

function firstDigit(value: number): number {
  return Math.floor(value / 10);
}
function secondDigit(value: number): number {
  return value % 10;
}

type CheckProps = {
  correct: boolean,
  onCheck: () => void,
};

const TICKS = 40;

class DigitosCheck extends React.PureComponent<
  CheckProps,
  { checking?: string, checked: boolean, tick: number }
> {
  _mounted: boolean;
  _timeout: ?TimeoutID;
  state = { checking: undefined, checked: !!this.props.correct, tick: 0 };

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
    }
  }

  _tick = () => {
    if (!this._mounted) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
      return;
    }
    this.setState(
      (state) => ({
        tick: state.tick + 1,
        checking: makeRandomId(2),
      }),
      () => {
        if (!this._mounted || this._timeout) return;
        if (this.state.tick === TICKS - 1) {
          // Check early so result is ready
          this.props.onCheck();
        }
        if (this.state.tick < TICKS) {
          this._timeout = setTimeout(() => {
            this._timeout = undefined;
            this._tick();
          }, 50);
        } else {
          // Finished
          this.setState(
            {
              tick: 0,
              checking: undefined,
              checked: true,
            },
            () => {
              this._timeout = setTimeout(() => {
                this.setState({ checked: false });
                this._timeout = undefined;
              }, 2000);
            }
          );
        }
      }
    );
  };

  _onClick = () => {
    const { correct } = this.props;
    const { checking, checked } = this.state;
    if (correct || checking || this._timeout) return;
    if (checked) {
      this.setState({ checked: false });
    } else {
      this._tick();
    }
  };

  render() {
    const { correct } = this.props;
    const { checking, checked } = this.state;
    const content = correct
      ? "OK"
      : checking != null
      ? checking
      : !checked
      ? "??"
      : "NO";
    return (
      <div
        className={classnames(
          styles.solucionDigito,
          styles.solucionCheck,
          correct ? styles.solucionCorrect : checked && styles.solucionError
        )}
        onClick={this._onClick}
      >
        {content}
      </div>
    );
  }
}

function makeRandomId(length: number) {
  let result = "";
  const characters = "0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

const DEFAULT_DIGITOS = [0, 0, 0];

function valueFromLocal(
  store: RegisteredStore,
  player: Player
): [number, number, number] {
  const digitos = store.local?.digitos;
  return (
    (digitos && digitos[player] && digitos[player].value) || DEFAULT_DIGITOS
  );
}

function valueFromEquipo(equipo: Equipo): [number, number, number] {
  return equipo.pruebas?.digitos?.value || DEFAULT_DIGITOS;
}
function correctFromEquipo(equipo: Equipo): boolean {
  return equipo.pruebas?.digitos?.correct || false;
}

function updateLocalWithDigitos(
  store: RegisteredStore,
  player: Player,
  value: LocalDigitos
): RegisteredStore {
  return localStoreWithPrueba(store, "digitos", {
    [(player: string)]: value,
  });
}
